import React, { useState, useEffect } from "react"
import { Card, Button, Space, Row, Col, Typography } from "antd"
import {
  PlusOutlined,
  FileSearchOutlined,
  SettingOutlined,
  UserAddOutlined,
  FileTextOutlined,
  DashboardOutlined,
  EditOutlined,
} from "@ant-design/icons"
import waterHeaterIcon from "assets/icons/water-heater.svg"
import { useHistory } from "react-router-dom"
import { userRoutes } from "routes/allRoutes"
import { getCurrentUser } from "helpers/service_helper"
import QuickActionsManager from "./QuickActionsManager"

const { Title } = Typography

const STORAGE_KEY = "quickActions"

// Icon mapping for dynamic icons
const iconMap = {
  "mdi mdi-view-dashboard": <DashboardOutlined />,
  "ti-settings": <SettingOutlined />,
  "mdi mdi-dns": <FileSearchOutlined />,
  "ti-files": <FileTextOutlined />,
  "ti-control-play": <PlusOutlined />,
  "ti-wallet": <DashboardOutlined />,
  "ti-comments": <FileTextOutlined />,
  "ti-file": <FileTextOutlined />,
  "ti-server": <DashboardOutlined />,
  "ti-user": <UserAddOutlined />,
  "mdi mdi-map": <DashboardOutlined />,
  "ti-layout-grid2": <DashboardOutlined />,
  "ti-map-alt": <DashboardOutlined />,
  WaterHeater: (
    <img
      src={waterHeaterIcon}
      alt="water heater"
      style={{ width: 16, height: 16 }}
    />
  ),
}

const QuickActions = ({ role }) => {
  const history = useHistory()
  const [managerVisible, setManagerVisible] = useState(false)
  const [selectedActions, setSelectedActions] = useState([])
  const [actions, setActions] = useState([])

  // Load saved actions from localStorage
  useEffect(() => {
    const user = getCurrentUser()
    const storageKey = `${STORAGE_KEY}_${user?.nameid || "default"}_${role}`
    const saved = localStorage.getItem(storageKey)

    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        setSelectedActions(parsed)
      } catch (error) {
        console.error("Failed to parse saved actions:", error)
        setSelectedActions([])
      }
    } else {
      // Set default actions based on role
      const defaults = getDefaultActionsByRole(role)
      setSelectedActions(defaults)
    }
  }, [role])

  // Build actions from selected route IDs
  useEffect(() => {
    if (selectedActions.length > 0) {
      const user = getCurrentUser()
      const userRole = user?.role

      const builtActions = selectedActions
        .map(routeId => {
          const route = userRoutes.find(r => r.id === routeId)
          if (!route || !route.roles?.includes(userRole)) return null

          return {
            icon: iconMap[route.icon] || <FileTextOutlined />,
            label: route.title,
            onClick: () => history.push(route.path),
            type: routeId === selectedActions[0] ? "primary" : "default",
          }
        })
        .filter(Boolean)

      setActions(builtActions)
    } else {
      // Fallback to default actions
      setActions(getActionsByRole())
    }
  }, [selectedActions, role, history])

  const getDefaultActionsByRole = role => {
    switch (role) {
      case "Administrator":
        return [100306, 1008, 1006, 100901] // GasProjectProcess, Support, Accounting, Reports
      case "Engineer":
        return [100305, 1008, 1006, 100903] // MyProjectProcess, Support, Accounting, Reports
      case "Executor":
        return [100301, 100303, 1008, 1006] // CreateProject, ProjectExecutor, Support, Accounting
      case "Installer":
        return [1024, 1018, 1008, 1016] // ProjectPackages, GasProjectInstaller, Support, Profile
      case "Nezarat":
        return [1026] // ProjectPackages, GasProjectInstaller, Support, Profile
      default:
        return []
    }
  }

  const getActionsByRole = () => {
    const commonActions = [
      {
        icon: <FileTextOutlined />,
        label: "گزارشات",
        onClick: () => history.push("/reports"),
        type: "default",
      },
    ]

    switch (role) {
      case "Administrator":
        return [
          {
            icon: <PlusOutlined />,
            label: "تخصیص",
            onClick: () => history.push("/projects/GasProjectProcess"),
            type: "primary",
          },
          {
            icon: <SettingOutlined />,
            label: "پشتیبانی",
            onClick: () => history.push("/support"),
            type: "default",
          },
          {
            icon: <DashboardOutlined />,
            label: "حسابداری",
            onClick: () => history.push("/accounting"),
            type: "default",
          },
          ...commonActions,
        ]
      case "Engineer":
        return [
          {
            icon: <PlusOutlined />,
            label: "پرونده جدید",
            onClick: () => history.push("/projects/new"),
            type: "primary",
          },
          {
            icon: <FileTextOutlined />,
            label: "پرونده‌های من",
            onClick: () => history.push("/projects/my-projects"),
            type: "default",
          },
          ...commonActions,
        ]
      case "Executor":
        return [
          {
            icon: <PlusOutlined />,
            label: "ثبت پرونده",
            onClick: () => history.push("/projects/new"),
            type: "primary",
          },
          {
            icon: <FileSearchOutlined />,
            label: "پرونده‌های من",
            onClick: () => history.push("/projects/my-projects"),
            type: "default",
          },
          ...commonActions,
        ]
      case "Installer":
        return [
          {
            icon: <PlusOutlined />,
            label: "پکیج‌های من",
            onClick: () => history.push("/project-packages"),
            type: "primary",
          },
          {
            icon: <FileSearchOutlined />,
            label: "لیست پرونده‌ها",
            onClick: () => history.push("/projects/GasProjectInstaller"),
            type: "default",
          },
          {
            icon: <SettingOutlined />,
            label: "پشتیبانی",
            onClick: () => history.push("/support"),
            type: "default",
          },
          ...commonActions,
        ]
      default:
        return commonActions
    }
  }

  const handleSaveActions = newActions => {
    const user = getCurrentUser()
    const storageKey = `${STORAGE_KEY}_${user?.nameid || "default"}_${role}`

    setSelectedActions(newActions)
    localStorage.setItem(storageKey, JSON.stringify(newActions))
  }

  return (
    <>
      <Card
        title={<Title level={5}>دسترسی سریع</Title>}
        style={{ borderRadius: "8px", height: "100%" }}
        extra={
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => setManagerVisible(true)}
            size="small"
          >
            ویرایش
          </Button>
        }
      >
        <Row gutter={[16, 16]}>
          {actions.map((action, index) => (
            <Col xs={24} sm={24} md={24} lg={24} key={index}>
              <Button
                type={action.type}
                icon={action.icon}
                onClick={action.onClick}
                block
                size="large"
                style={{
                  height: "auto",
                  padding: "12px 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {action.label}
              </Button>
            </Col>
          ))}
        </Row>
      </Card>

      <QuickActionsManager
        visible={managerVisible}
        onClose={() => setManagerVisible(false)}
        onSave={handleSaveActions}
        currentActions={selectedActions}
      />
    </>
  )
}

export default QuickActions
